import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { toast } from 'sonner';
import {
  DataSource,
  DataSourcesListResponse,
  CreateDataSourceRequest,
  UpdateDataSourceRequest,
  DuplicateDataSourceRequest,
  TestConnectionRequest,
  TestConnectionResponse,
  ExtractionJobsResponse,
  DataSourceQueryHints,
  DataSourceType,
  UploadFileResponse,
} from '@/types/data-source';

// Query Keys
export const dataSourceKeys = {
  all: ['dataSources'] as const,
  lists: () => [...dataSourceKeys.all, 'list'] as const,
  list: (filters: string) => [...dataSourceKeys.lists(), { filters }] as const,
  details: () => [...dataSourceKeys.all, 'detail'] as const,
  detail: (id: string) => [...dataSourceKeys.details(), id] as const,
  queryHints: (id: string) => [...dataSourceKeys.detail(id), 'query-hints'] as const,
  extractions: (id: string) => [...dataSourceKeys.detail(id), 'extractions'] as const,
};

// Fetch all data sources
export function useDataSources(params: {
  page: number;
  limit: number;
  type?: DataSourceType;
  search?: string;
}) {
  return useQuery({
    queryKey: dataSourceKeys.list(JSON.stringify(params)),
    queryFn: async () => {
      const query = new URLSearchParams();
      query.set('page', String(params.page));
      query.set('limit', String(params.limit));
      if (params.type) {
        query.set('type', params.type);
      }
      if (params.search) {
        query.set('search', params.search);
      }
      const response = await apiClient.get<DataSourcesListResponse>(
        `/data-sources?${query.toString()}`
      );
      return response;
    },
  });
}

// Fetch single data source
export function useDataSource(id: string) {
  return useQuery({
    queryKey: dataSourceKeys.detail(id),
    queryFn: async () => {
      const response = await apiClient.get<DataSource>(`/data-sources/${id}`);
      return response;
    },
    enabled: !!id,
  });
}

// Fetch query hints (safe schema/table/view references)
export function useDataSourceQueryHints(id: string) {
  return useQuery({
    queryKey: dataSourceKeys.queryHints(id),
    queryFn: async () => {
      const response = await apiClient.get<DataSourceQueryHints>(
        `/data-sources/${id}/query-hints`
      );
      return response;
    },
    enabled: !!id,
  });
}

// Fetch extraction history for a data source
// Auto-polls every 3s while any job is running/pending, stops when all are done
export function useExtractionHistory(dataSourceId: string) {
  return useQuery({
    queryKey: dataSourceKeys.extractions(dataSourceId),
    queryFn: async () => {
      const query = new URLSearchParams();
      query.set('limit', '50');
      if (dataSourceId) {
        query.set('dataSourceId', dataSourceId);
      }
      const response = await apiClient.get<ExtractionJobsResponse>(`/extraction/jobs?${query.toString()}`);
      return response;
    },
    enabled: !!dataSourceId,
    refetchInterval: (query) => {
      const jobs = query.state.data?.jobs;
      if (!jobs) return false;
      const hasActive = jobs.some((j) => j.status === 'running' || j.status === 'pending');
      return hasActive ? 3000 : false;
    },
  });
}

// Create data source
export function useCreateDataSource() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateDataSourceRequest) => {
      const response = await apiClient.post<DataSource>('/data-sources', data);
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: dataSourceKeys.lists() });
      toast.success('Data source created successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to create data source');
    },
  });
}

// Upload a CSV/XLSX file for a FILE_UPLOAD data source. Returns the uploadId +
// detected columns/rowCount to reference when creating the source.
export function useUploadDataSourceFile() {
  return useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append('file', file);
      const response = await apiClient.post<UploadFileResponse>(
        '/data-sources/upload',
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      return response;
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to upload file');
    },
  });
}

// Update data source
export function useUpdateDataSource(id: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: UpdateDataSourceRequest) => {
      const response = await apiClient.put<DataSource>(`/data-sources/${id}`, data);
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: dataSourceKeys.lists() });
      queryClient.invalidateQueries({ queryKey: dataSourceKeys.detail(id) });
      toast.success('Data source updated successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to update data source');
    },
  });
}

// Duplicate data source
export function useDuplicateDataSource() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: { id: string; data?: DuplicateDataSourceRequest }) => {
      const response = await apiClient.post<DataSource>(
        `/data-sources/${payload.id}/duplicate`,
        payload.data || {}
      );
      return response;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: dataSourceKeys.lists() });
      toast.success(`Data source duplicated as "${data.name}"`);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to duplicate data source');
    },
  });
}

// Delete data source
export function useDeleteDataSource() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/data-sources/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: dataSourceKeys.lists() });
      toast.success('Data source deleted successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to delete data source');
    },
  });
}

// Test connection
export function useTestConnection() {
  return useMutation({
    mutationFn: async (data: TestConnectionRequest) => {
      const response = await apiClient.post<TestConnectionResponse>('/data-sources/test', data);
      return response;
    },
    onSuccess: (data) => {
      if (data.success) {
        toast.success(data.message || 'Connection test successful');
      } else {
        toast.error(data.message || 'Connection test failed');
      }
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Connection test failed');
    },
  });
}

// Discover Tableau workbook
export interface TableauDiscoverRequest {
  url: string;
  parameters?: Record<string, string>;
  filters?: Array<{ worksheet: string; column: string; value: string }>;
  requestDelaySeconds?: number;
}

export interface TableauDiscoverResponse {
  worksheets: Array<{ name: string; columns: string[]; rowCount: number }>;
  parameters: Array<{ name: string; values: string[] }>;
}

export function useDiscoverTableau() {
  return useMutation({
    mutationFn: async (data: TableauDiscoverRequest) => {
      return apiClient.post<TableauDiscoverResponse>('/data-sources/tableau/discover', data);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Tableau discovery failed');
    },
  });
}

// Trigger extraction
export function useTriggerExtraction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: { dataSourceId: string; query: string }) => {
      await apiClient.post('/extraction/start', payload);
      return payload.dataSourceId;
    },
    onSuccess: (dataSourceId) => {
      // Invalidate extraction history so polling starts immediately
      queryClient.invalidateQueries({ queryKey: dataSourceKeys.extractions(dataSourceId) });
      toast.success('Extraction job started');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to start extraction');
    },
  });
}
